import { createModel } from '@rematch/core';
import Axios from 'axios';
import { Term } from './paymentTerms';

export type CurrencyPayOpenParams = {
  total: number;
  items: CurrencyPayItem[];
  orderId: string | null;
  orderDesc: string | null;
  tax: number | null;
  shipping: number | null;
};

export type CurrencyPayItem = {
  itemId: string;
  itemDescription: string;
  itemPrice: number;
  itemQty: number;
  um: string | 'each';
  productCode: string;
};

export interface CurrencyPayResponse {
  data: [
    {
      txnId: string;
      payment: {
        method: number;
      };
    }
  ];
}

export interface CurrencyPayFinancingResponse {
  applicationId: number;
  applicationStatus: string;
}

export type GetConfigSettings = {
  dealerId: string;
  currencyPaySuccess: (responseData: CurrencyPayResponse[]) => void;
  currencyPayUnderReview: (
    responseData: CurrencyPayFinancingResponse[]
  ) => void;
  currencyPayFailure: () => void;
};

type PaymentStatus = {
  quoteId: string;
  paymentStatus: string;
  transactionId: string | null;
  paymentMethod: number;
  selectedTerms: number[];
  companyId: string;
};

type PaymentMethods = {
  CreditCard: boolean;
  BankWire: boolean;
  ExpressFinancing: boolean;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Window {
    CurrencyPayWindow: {
      config: CurrencyPayConfig;
      destroy?: () => void;
      open: (params: CurrencyPayOpenParams) => void;
      onSuccess: () => void;
      onFailure: () => void;
      onUnderReview: () => void;
      onInit: () => void;
    };
    Commerce: any; // eslint-disable-line
  }
}

interface DealerAPICurrencyPayData {
  publicApiKey: string;
  merchantId: string;
  merchantCode: string;
  paymentMethods: {
    AllowCreditCard: boolean;
    AllowBankWire: boolean;
    AllowExpressFinancing: boolean;
  };
}

type CurrencyPayConfig = {
  apiKey: string;
  financingEnabled: boolean;
  merchantId: string;
  merchantCode: string;
  onSuccess?: () => void;
  onFailure?: () => void;
  onUnderReview?: () => void;
  onInit?: () => void;
  paymentMethods: {
    CreditCard: boolean;
    BankWire: boolean;
    ExpressFinancing: boolean;
  };
};

type CurrencyPayState = {
  config: CurrencyPayConfig;
  isLoaded: boolean;
  scriptIsLoaded: boolean;
  isInitialized: boolean;
  isDefaultAccount: boolean;
};

const defaultState: CurrencyPayState = {
  config: {
    apiKey: '',
    financingEnabled: false,
    merchantId: '',
    merchantCode: '',
    paymentMethods: {
      CreditCard: false,
      BankWire: false,
      ExpressFinancing: false
    }
  },
  isLoaded: false,
  scriptIsLoaded: false,
  isInitialized: false,
  isDefaultAccount: false
};

const setCurrencyPayConfig = function handleSetCurrencyPayConfig(
  state: CurrencyPayState,
  apiData: DealerAPICurrencyPayData
): CurrencyPayState {
  const config = {
    ...state.config,
    apiKey: apiData.publicApiKey,
    financingEnabled:
      state?.config?.financingEnabled ?? false
        ? true
        : state?.config?.paymentMethods?.ExpressFinancing ?? false,
    merchantCode: apiData.merchantCode,
    merchantId: apiData.merchantId,
    paymentMethods: state.config.paymentMethods
  };

  return {
    ...state,
    config
  };
};

const setCurrencyPayIsLoaded = function handleSetCurrencyPayConfig(
  state: CurrencyPayState,
  isLoaded: boolean
): CurrencyPayState {
  return {
    ...state,
    isLoaded
  };
};

const setIsDefaultAccount = function handleIsDefaultAccount(
  state: CurrencyPayState,
  isDefaultAccount: boolean
): CurrencyPayState {
  return {
    ...state,
    isDefaultAccount
  };
};

const scriptIsLoaded = function setScriptIsLoaded(
  state: CurrencyPayState
): CurrencyPayState {
  return {
    ...state,
    scriptIsLoaded: true
  };
};

const isInitialized = function setIsInitialized(
  state: CurrencyPayState
): CurrencyPayState {
  return {
    ...state,
    isInitialized: true
  };
};

const setPaymentMethods = function handleSetPaymentMethods(
  state: CurrencyPayState,
  paymentMethods: PaymentMethods
): CurrencyPayState {
  return {
    ...state,
    config: {
      ...state.config,
      paymentMethods
    }
  };
};

type CurrencyPayReinitialize = {
  onSuccess: () => void;
  onFailure: () => void;
  onUnderReview: () => void;
  onInit: () => void;
};

const reinitialize = function reinitializeCurrencyWidget(
  state: CurrencyPayState,
  initialize: CurrencyPayReinitialize
): CurrencyPayState {
  const onSuccess = initialize.onSuccess || state.config.onSuccess;
  const onFailure = initialize.onFailure || state.config.onFailure;
  const onUnderReview = initialize.onUnderReview || state.config.onUnderReview;
  const onInit = initialize.onInit || state.config.onInit;
  const currencyPay = {
    config: state.config,
    open: window.CurrencyPayWindow && window.CurrencyPayWindow.open,
    onSuccess,
    onFailure,
    onUnderReview,
    onInit
  };
  window.CurrencyPayWindow = currencyPay;

  const event = new CustomEvent('initializeWidget');
  window.document.dispatchEvent(event);

  return {
    ...state,
    isInitialized: false,
    config: { ...state.config, onSuccess, onFailure, onUnderReview, onInit }
  };
};
const resetFinancing = function resetFinancingFromApiDefault(
  state: CurrencyPayState
): CurrencyPayState {
  return {
    ...state,
    config: {
      ...state.config,
      paymentMethods: {
        CreditCard: state.config.paymentMethods.CreditCard,
        BankWire: state.config.paymentMethods.BankWire,
        ExpressFinancing: state.config.financingEnabled
      }
    }
  };
};

const currencyPay = createModel({
  state: defaultState,
  reducers: {
    setCurrencyPayConfig,
    setCurrencyPayIsLoaded,
    setIsDefaultAccount,
    scriptIsLoaded,
    isInitialized,
    reinitialize,
    resetFinancing,
    setPaymentMethods
  },
  effects: {
    async getConfig(dealerId: string): Promise<void> {
      try {
        const dealerApi = Axios.create({
          baseURL: window.AutoQuotes.config.apiBaseUrl
        });

        const { data: apiData } = await dealerApi.get(
          `company/${dealerId}/pay`
        );

        this.setCurrencyPayConfig(apiData);

        if (apiData.publicApiKey) {
          this.setCurrencyPayIsLoaded(true);
          this.setIsDefaultAccount(apiData.isDefaultAccount);
        }
      } catch (err) {
        this.setCurrencyPayIsLoaded(false);
      }
    },
    async savePaymentStatus(payment: PaymentStatus): Promise<Term[]> {
      const {
        quoteId,
        paymentStatus,
        transactionId,
        paymentMethod,
        selectedTerms,
        companyId
      } = payment;

      const paymentStatusApi = Axios.create({
        baseURL: window.AutoQuotes.config.apiBaseUrl
      });

      const { data } = await paymentStatusApi.post(`quote/${quoteId}/payment`, {
        paymentStatus,
        transactionId,
        paymentMethod,
        selectedTerms,
        companyId
      });

      return data;
    }
  }
});

export default currencyPay;
